// src/services/review.js

// Base API URL - should match provider.js
const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY

/**
 * Fetch all reviews for a specific provider
 * @param {string} providerId - ID of the provider
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Number of items per page (default: 10)
 * @param {string} lang - Language for the reviews (default: 'english')
 * @returns {Promise<Object>} - Object containing reviews array and pagination data
 */
export async function fetchProviderReviews(providerId, page = 1, pageSize = 10, lang = 'english') {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  params.append('lang', lang);
  
  const response = await fetch(`${API_BASE_URL}/reviews/provider/${providerId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  return {
    reviews: data.reviews || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Fetch review summary for a provider
 * @param {string} providerId - ID of the provider
 * @returns {Promise<Object>} - Summary object with average ratings
 */
export async function fetchProviderReviewSummary(providerId) {
  const response = await fetch(`${API_BASE_URL}/reviews/summary/${providerId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Create a new review for a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider to review
 * @param {number} rating - Overall rating (1-5)
 * @param {string} reviewText - Optional review text
 * @param {Object} additionalRatings - Optional detailed ratings
 * @returns {Promise<Object>} - Created review object
 */
export async function createReview(token, providerId, rating, reviewText = null, additionalRatings = {}) {
  const { communicationRating, knowledgeRating, timelinessRating } = additionalRatings;
  
  const response = await fetch(`${API_BASE_URL}/reviews/protected`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      provider_id: providerId,
      rating,
      review_text: reviewText,
      communication_rating: communicationRating,
      knowledge_rating: knowledgeRating,
      timeliness_rating: timelinessRating
    })
  });
  
  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create review');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Update an existing review
 * @param {string} token - JWT authentication token
 * @param {string} reviewId - ID of the review to update
 * @param {number} rating - Overall rating (1-5)
 * @param {string} reviewText - Optional review text
 * @param {Object} additionalRatings - Optional detailed ratings
 * @returns {Promise<Object>} - Updated review object
 */
export async function updateReview(token, reviewId, rating, reviewText = null, additionalRatings = {}) {
  const { communicationRating, knowledgeRating, timelinessRating } = additionalRatings;
  
  const response = await fetch(`${API_BASE_URL}/reviews/protected/${reviewId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      rating,
      review_text: reviewText,
      communication_rating: communicationRating,
      knowledge_rating: knowledgeRating,
      timeliness_rating: timelinessRating
    })
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Review not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to update this review');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Delete a review
 * @param {string} token - JWT authentication token
 * @param {string} reviewId - ID of the review to delete
 * @returns {Promise<void>}
 */
export async function deleteReview(token, reviewId) {
  const response = await fetch(`${API_BASE_URL}/reviews/protected/${reviewId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Review not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized to delete this review');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  // DELETE typically returns no content
  return;
}

/**
 * Fetch all reviews created by the current user
 * @param {string} token - JWT authentication token
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Number of items per page (default: 10)
 * @param {string} lang - Language for the reviews (default: 'english')
 * @returns {Promise<Object>} - Object containing user's reviews and pagination data
 */
export async function fetchMyReviews(token, page = 1, pageSize = 10, lang = 'english') {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  params.append('lang', lang);
  
  const response = await fetch(`${API_BASE_URL}/reviews/my-reviews?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  return {
    reviews: data.reviews || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Get the current user's review for a specific provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider
 * @returns {Promise<Object|null>} - Review object or null if no review exists
 */
export async function fetchMyReviewForProvider(token, providerId) {
  try {
    const response = await fetch(`${API_BASE_URL}/reviews/protected/my-review/${providerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (response.status === 404) {
      return null; // User hasn't reviewed this provider yet
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user review:', error);
    return null;
  }
}

/**
 * Check if current user has already reviewed a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - ID of the provider
 * @returns {Promise<boolean>} - True if user has already reviewed
 */
export async function hasReviewedProvider(token, providerId) {
  try {
    const review = await fetchMyReviewForProvider(token, providerId);
    return review !== null;
  } catch (error) {
    console.error('Error checking review status:', error);
    return false;
  }
}

/**
 * Submit a provider's response to a review
 * @param {string} token - JWT authentication token
 * @param {string} reviewId - ID of the review to respond to
 * @param {string} responseText - Provider's response text
 * @returns {Promise<Object>} - Updated review object including the provider response
 */
export async function submitProviderResponse(token, reviewId, responseText) {
  const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}/provider-response`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      response_text: responseText
    })
  });
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Review not found');
    } else if (response.status === 401) {
      throw new Error('Unauthorized - only providers can respond to reviews');
    } else if (response.status === 403) {
      throw new Error('Forbidden - you can only respond to reviews for your practice');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}